'use client'

import { useState } from 'react'
import {
  CheckboxField,
  FieldGroup,
  FieldHint,
  FieldLabel,
  FormTooltip,
  LabelWithTooltip,
  PrimaryButton,
  RangeField,
  SelectField,
  TextareaField,
  TextField,
} from '@/app/FormFields'

interface ConfigFormProps {
  initial: {
    submissionPromptText: string
    additionalInfoPromptText: string
    submissionMaxChars: number
    aiProvider: string
    aiModel: string
    aiTemperature: number
    minInfoScoreThreshold: number
    maxClarificationCycles: number
    aiJobTimeoutSeconds: number
    dataProcessingConsent: boolean
  }
}

const AI_MODELS = [
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (fast, low cost)' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (balanced)' },
  { value: 'claude-opus-4-7', label: 'Claude Opus 4.7 (most capable)' },
]

export default function ConfigForm({ initial }: ConfigFormProps) {
  const [values, setValues] = useState(initial)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function set<K extends keyof typeof values>(key: K, value: typeof values[K]) {
    setValues((v) => ({ ...v, [key]: value }))
    setSuccess(false)
    setError(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setSuccess(false)
    setError(null)
    try {
      const res = await fetch('/api/admin/config', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(data.error ?? 'Failed to save config')
      } else {
        setSuccess(true)
      }
    } catch {
      setError('Network error — please try again')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">

      {/* UI Copy */}
      <section className="space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Submission UI</h2>
        <FieldGroup>
          <FieldLabel>Submission prompt text</FieldLabel>
          <TextareaField
            rows={3}
            value={values.submissionPromptText}
            onChange={(e) => set('submissionPromptText', e.target.value)}
          />
        </FieldGroup>
        <FieldGroup>
          <FieldLabel>Additional info prompt text</FieldLabel>
          <TextareaField
            rows={3}
            value={values.additionalInfoPromptText}
            onChange={(e) => set('additionalInfoPromptText', e.target.value)}
          />
        </FieldGroup>
        <FieldGroup>
          <FieldLabel>Max submission characters</FieldLabel>
          <FieldHint>500–20 000</FieldHint>
          <TextField
            type="number"
            min={500}
            max={20000}
            value={values.submissionMaxChars}
            onChange={(e) => set('submissionMaxChars', Number(e.target.value))}
          />
        </FieldGroup>
      </section>

      {/* AI Settings */}
      <section className="space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">AI Settings</h2>
        <FieldGroup>
          <LabelWithTooltip
            tooltipId="ai-model-help"
            tooltipText="Controls which model handles completeness checks, summaries, drafts, and knowledge-base proposals for this tenant."
          >
            AI model
          </LabelWithTooltip>
          <SelectField
            value={values.aiModel}
            onChange={(e) => set('aiModel', e.target.value)}
            aria-describedby="ai-model-help"
          >
            {AI_MODELS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </SelectField>
        </FieldGroup>
        <FieldGroup>
          <LabelWithTooltip
            tooltipId="ai-temperature-help"
            tooltipText="Lower values make AI output more consistent. Higher values make it more varied, which can be useful for drafting but riskier for regulated guidance."
          >
            Temperature
          </LabelWithTooltip>
          <FieldHint>0.0 (deterministic) – 1.0 (creative)</FieldHint>
          <div className="flex items-center gap-3">
            <RangeField
              min={0}
              max={1}
              step={0.05}
              value={values.aiTemperature}
              onChange={(e) => set('aiTemperature', Number(e.target.value))}
              className="flex-1"
              aria-describedby="ai-temperature-help"
            />
            <span className="w-10 text-right text-sm font-mono text-zinc-700 dark:text-zinc-300">
              {values.aiTemperature.toFixed(2)}
            </span>
          </div>
        </FieldGroup>
        <FieldGroup>
          <LabelWithTooltip
            tooltipId="min-info-score-help"
            tooltipText="Raises or lowers how strict the completeness agent is before asking the line manager for more information."
          >
            Min info score threshold
          </LabelWithTooltip>
          <FieldHint>0.0–1.0 — tickets below this score trigger clarification</FieldHint>
          <div className="flex items-center gap-3">
            <RangeField
              min={0}
              max={1}
              step={0.05}
              value={values.minInfoScoreThreshold}
              onChange={(e) => set('minInfoScoreThreshold', Number(e.target.value))}
              className="flex-1"
              aria-describedby="min-info-score-help"
            />
            <span className="w-10 text-right text-sm font-mono text-zinc-700 dark:text-zinc-300">
              {values.minInfoScoreThreshold.toFixed(2)}
            </span>
          </div>
        </FieldGroup>
        <FieldGroup>
          <FieldLabel>Max clarification cycles</FieldLabel>
          <FieldHint>0–10</FieldHint>
          <TextField
            type="number"
            min={0}
            max={10}
            value={values.maxClarificationCycles}
            onChange={(e) => set('maxClarificationCycles', Number(e.target.value))}
          />
        </FieldGroup>
        <FieldGroup>
          <FieldLabel>AI job timeout (seconds)</FieldLabel>
          <FieldHint>30–600</FieldHint>
          <TextField
            type="number"
            min={30}
            max={600}
            value={values.aiJobTimeoutSeconds}
            onChange={(e) => set('aiJobTimeoutSeconds', Number(e.target.value))}
          />
        </FieldGroup>
      </section>

      {/* Consent */}
      <section className="space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Data & Compliance</h2>
        <div className="flex items-start gap-3">
          <label className="flex cursor-pointer items-start gap-3">
            <CheckboxField
              checked={values.dataProcessingConsent}
              onChange={(e) => set('dataProcessingConsent', e.target.checked)}
              aria-describedby="data-processing-consent-help"
            />
            <span className="text-sm text-zinc-700 dark:text-zinc-300">
              Data processing consent — allow ticket content to be sent to the AI provider
            </span>
          </label>
          <FormTooltip
            id="data-processing-consent-help"
            text="When disabled, tenant ticket content should not be sent to the configured AI provider; experts can still handle tickets manually."
          />
        </div>
      </section>

      <div className="flex items-center gap-4">
        <PrimaryButton
          type="submit"
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </PrimaryButton>
        {success && <p className="text-sm text-green-700 dark:text-green-400">Saved successfully.</p>}
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>

    </form>
  )
}
